import {
  Box,
  Button,
  Flex,
  Heading,
  Skeleton,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MdAssignment, MdHomeWork, MdQuiz } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import StudentPortalShell from "../../../components/student/StudentPortalShell";
import PdfPreviewModal from "../../../components/student/PdfPreviewModal";
import { studentPalette } from "../../../constants/studentPalette";
import { useAdminTheme } from "../../../context/AdminThemeContext";
import { studentSurfaces } from "../../../constants/studentThemeSurfaces";
import { getAllExamsAndHomeworksRequest } from "../../../modules/students-modules/exams/Actions";
import {
  ChapterContentGrid,
  CourseStatsGrid,
  getHeroStyles,
} from "../courses/course-details";

const C = {
  ...studentPalette,
  border: studentSurfaces.border,
  muted: studentSurfaces.textMuted,
};

const TAB_HOMEWORK = "homework";
const TAB_QUIZZES = "quizzes";
const TAB_EXAMS = "exams";

const isQuizType = (ex) => Number(ex?.type) === 0;

const isFormalExamType = (ex) => {
  const t = Number(ex?.type);
  return t === 1 || t === 6;
};

const isHomeworkExamType = (ex) => Number(ex?.type) === 2;

const StudentQuickLinks = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { themeMode } = useAdminTheme();
  const hero = useMemo(() => getHeroStyles(themeMode === "dark"), [themeMode]);

  const allExams = useSelector((s) => s.chapterExams.allExams) || [];
  const allHomeworks = useSelector((s) => s.chapterExams.allHomeworks) || [];
  const isLoading = useSelector((s) => s.chapterExams.isLoading);
  const userData = useSelector((s) => s.user.userData);

  const tabParam = searchParams.get("tab");
  const activeTab =
    tabParam === TAB_EXAMS
      ? TAB_EXAMS
      : tabParam === TAB_QUIZZES
        ? TAB_QUIZZES
        : TAB_HOMEWORK;
  const contentFilter =
    activeTab === TAB_HOMEWORK
      ? "homework"
      : activeTab === TAB_QUIZZES
        ? "quizzes"
        : "exams";

  const [pdfPreview, setPdfPreview] = useState(null);

  const refreshExams = useCallback(() => {
    dispatch(getAllExamsAndHomeworksRequest());
  }, [dispatch]);

  useEffect(() => {
    refreshExams();
  }, [refreshExams]);

  const quizItems = useMemo(
    () => allExams.filter(isQuizType),
    [allExams]
  );
  const examItems = useMemo(
    () => allExams.filter(isFormalExamType),
    [allExams]
  );
  const classifiedExams = useMemo(
    () => allExams.filter(isHomeworkExamType),
    [allExams]
  );

  const stats = useMemo(() => {
    const homeworksCount = allHomeworks.length + classifiedExams.length;
    const examsCount = quizItems.length + examItems.length;
    const pendingHomeworksCount = allHomeworks.filter((hw) => {
      const status = hw?.student_homework?.status;
      return status == null || status === 0 || status === 2;
    }).length;
    const pendingAssessments = [...quizItems, ...examItems].filter((ex) => {
      const submitted =
        ex?.student_pdf != null && String(ex.student_pdf).trim() !== "";
      return !submitted;
    }).length;

    return {
      homeworksCount,
      examsCount,
      pendingHomeworksCount,
      pendingExamsCount: pendingAssessments,
    };
  }, [allHomeworks, classifiedExams, quizItems, examItems]);

  const contentBucket = useMemo(() => {
    if (activeTab === TAB_HOMEWORK) {
      return {
        loaded: true,
        loading: isLoading,
        exams: classifiedExams,
        homeworks: allHomeworks,
        videos: [],
        lessons: [],
        live_videos: [],
      };
    }
    if (activeTab === TAB_QUIZZES) {
      return {
        loaded: true,
        loading: isLoading,
        exams: quizItems,
        homeworks: [],
        videos: [],
        lessons: [],
        live_videos: [],
      };
    }
    return {
      loaded: true,
      loading: isLoading,
      exams: examItems,
      homeworks: [],
      videos: [],
      lessons: [],
      live_videos: [],
    };
  }, [activeTab, isLoading, classifiedExams, allHomeworks, quizItems, examItems]);

  const setActiveTab = (tab) => {
    setSearchParams({ tab });
  };

  const tabButtonProps = (tab) => ({
    variant: "unstyled",
    px: { base: 5, md: 8 },
    py: 3,
    fontWeight: "800",
    fontSize: "sm",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: activeTab === tab ? C.primary : C.muted,
    borderBottomWidth: "3px",
    borderColor: activeTab === tab ? C.primary : "transparent",
    borderRadius: 0,
    _hover: { color: C.primary },
  });

  const quickLinksReturnPath = `/students/quick-links?tab=${activeTab}`;

  return (
    <StudentPortalShell>
      <Flex direction="column" minH={{ md: "100vh" }}>
        <Box
          bg={hero.bg}
          w="100%"
          py={{ base: 10, md: 16 }}
          px={{ base: 4, md: 12 }}
          borderBottomWidth="1px"
          borderColor={hero.border}
        >
          <Box maxW="5xl" mx="auto" w="100%" color={hero.text}>
            <Text
              fontSize="xs"
              fontWeight="800"
              letterSpacing="0.2em"
              textTransform="uppercase"
              color={hero.textMuted}
              mb={2}
            >
              Quick access
            </Text>
            <Heading
              as="h1"
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="800"
              color={hero.text}
              textTransform="uppercase"
              letterSpacing="tight"
              lineHeight="1.15"
              mb={6}
            >
              Quick Links
            </Heading>

            <CourseStatsGrid
              sessionsCount={0}
              liveCount={0}
              pdfsCount={0}
              examsCount={stats.examsCount}
              homeworksCount={stats.homeworksCount}
              pendingExamsCount={stats.pendingExamsCount}
              pendingHomeworksCount={stats.pendingHomeworksCount}
              hero={hero}
            />
          </Box>
        </Box>

        <Box maxW="4xl" mx="auto" py={{ base: 10, md: 16 }} px={{ base: 4, md: 6 }} w="100%">
          <Flex
            align="center"
            gap={2}
            borderBottomWidth="2px"
            borderColor={C.border}
            mb={10}
            flexWrap="wrap"
          >
            <Button
              {...tabButtonProps(TAB_HOMEWORK)}
              onClick={() => setActiveTab(TAB_HOMEWORK)}
              leftIcon={<MdHomeWork size={18} />}
            >
              Homework
            </Button>
            <Button
              {...tabButtonProps(TAB_QUIZZES)}
              onClick={() => setActiveTab(TAB_QUIZZES)}
              leftIcon={<MdQuiz size={18} />}
            >
              Quizzes
            </Button>
            <Button
              {...tabButtonProps(TAB_EXAMS)}
              onClick={() => setActiveTab(TAB_EXAMS)}
              leftIcon={<MdAssignment size={18} />}
            >
              Exams
            </Button>
          </Flex>

          <Flex
            align="center"
            justify="space-between"
            borderBottomWidth="2px"
            borderColor={C.primary}
            pb={4}
            mb={10}
            flexWrap="wrap"
            gap={4}
          >
            <Heading
              fontSize="2xl"
              fontWeight="800"
              color={C.primary}
              textTransform="uppercase"
              letterSpacing="tight"
            >
              {activeTab === TAB_HOMEWORK
                ? "Homework & classified"
                : activeTab === TAB_QUIZZES
                  ? "Quizzes"
                  : "Exams"}
            </Heading>
            <Text fontSize="sm" fontWeight="600" color={C.muted}>
              {activeTab === TAB_HOMEWORK
                ? "Homework assignments and classified exams"
                : activeTab === TAB_QUIZZES
                  ? "Quizzes"
                  : "Exams and video exams"}
            </Text>
          </Flex>

          {isLoading && allExams.length === 0 && allHomeworks.length === 0 ? (
            <VStack spacing={4} align="stretch">
              {[1, 2, 3].map((k) => (
                <Skeleton key={k} h="200px" borderRadius="xl" />
              ))}
            </VStack>
          ) : (
            <Box
              bg={studentSurfaces.raised}
              borderWidth="1px"
              borderColor={C.border}
              borderRadius="xl"
              overflow="hidden"
            >
              <ChapterContentGrid
                bucket={contentBucket}
                chapter={null}
                userData={userData}
                courseReturnPath={quickLinksReturnPath}
                onOpenPdf={setPdfPreview}
                contentFilter={contentFilter}
                onContentRefresh={refreshExams}
                emptyMessage={
                  activeTab === TAB_HOMEWORK
                    ? "No homework or classified exams yet."
                    : activeTab === TAB_QUIZZES
                      ? "No quizzes yet."
                      : "No exams yet."
                }
              />
            </Box>
          )}
        </Box>
      </Flex>

      <PdfPreviewModal
        isOpen={Boolean(pdfPreview?.url)}
        onClose={() => setPdfPreview(null)}
        pdfUrl={pdfPreview?.url || ""}
        title={pdfPreview?.title}
        backLabel={pdfPreview?.backLabel}
        totalPages={pdfPreview?.totalPages}
      />
    </StudentPortalShell>
  );
};

export default StudentQuickLinks;
